import { ReactNode, useRef, useEffect, useState } from "react";
import styled from "styled-components";

type MapMouseTooltipProps = {
  x: number;
  y: number;
  children: ReactNode;
  offsetX?: number;
  offsetY?: number;
  mapRef?: React.RefObject<HTMLDivElement>;
};

export default function MapMouseTooltip({
  x,
  y,
  children,
  offsetX = 20,
  offsetY = 0,
  mapRef
}: MapMouseTooltipProps) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: 0, top: 0 });

  useEffect(() => {
    // Calculate position relative to the map container if mapRef is provided
    if (mapRef?.current && tooltipRef.current) {
      const mapRect = mapRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      // Adjust position to stay within the map boundaries
      let adjustedX = x + offsetX;
      let adjustedY = y + offsetY;

      // Check if tooltip would go beyond right edge
      if (adjustedX + tooltipRect.width > mapRect.right) {
        adjustedX = x - tooltipRect.width - offsetX;
      }

      // Check if tooltip would go beyond bottom edge
      if (adjustedY + tooltipRect.height > mapRect.bottom) {
        adjustedY = y - tooltipRect.height - offsetY;
      }

      // Set position based on map's position
      setPosition({
        left: adjustedX - mapRect.left,
        top: adjustedY - mapRect.top
      });
    } else {
      // No map reference, use direct coordinates
      setPosition({
        left: x + offsetX,
        top: y + offsetY
      });
    }
  }, [x, y, offsetX, offsetY, mapRef]);

  return (
    <Container
      ref={tooltipRef}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`
      }}
    >
      {children}
    </Container>
  );
}

const Container = styled.div`
  position: absolute;
  z-index: 1000;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
`;
